import jsPDF from 'jspdf';
import type { CategoryScores } from './quizData';
import {
  getCategoryExplanation,
  getCategoryLabel,
  getWeakAreas,
  getDynamicRecommendation,
  getPdfDiagnosis,
} from './roadmapContent';

interface UserData {
  fullName: string;
  email: string;
}

interface ScoreData {
  total: number;
  categories: CategoryScores;
}

// Brand colors
const ORANGE = [252, 100, 36] as const; // #fc6424
const DARK = [23, 23, 23] as const;
const MID_GRAY = [120, 120, 120] as const;
const LIGHT_BG = [248, 248, 248] as const;
const WHITE = [255, 255, 255] as const;

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 20;
const CONTENT_W = PAGE_W - MARGIN * 2;

function setColor(doc: jsPDF, rgb: readonly [number, number, number]) {
  doc.setTextColor(rgb[0], rgb[1], rgb[2]);
}

function drawProgressBar(doc: jsPDF, x: number, y: number, w: number, h: number, pct: number) {
  // Background
  doc.setFillColor(235, 235, 235);
  doc.roundedRect(x, y, w, h, h / 2, h / 2, 'F');
  // Fill
  if (pct > 0) {
    doc.setFillColor(...ORANGE);
    doc.roundedRect(x, y, Math.max(w * pct, h), h, h / 2, h / 2, 'F');
  }
}

function addFooter(doc: jsPDF) {
  const pageNum = doc.getNumberOfPages();
  for (let i = 1; i <= pageNum; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    setColor(doc, MID_GRAY);
    doc.text('vantagepointmediagroup.com', PAGE_W / 2, PAGE_H - 10, { align: 'center' });
    doc.text(`Page ${i} of ${pageNum}`, PAGE_W - MARGIN, PAGE_H - 10, { align: 'right' });
  }
}

function addPageLine(doc: jsPDF) {
  doc.setDrawColor(...ORANGE);
  doc.setLineWidth(0.8);
  doc.line(MARGIN, 12, PAGE_W - MARGIN, 12);
}

export function generateRoadmapPDF(
  userData: UserData,
  quizAnswers: Record<string, string | string[]>,
  scoreData: ScoreData
): jsPDF {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const firstName = userData.fullName.split(' ')[0] || 'Your';
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  // ===== PAGE 1: COVER =====
  addPageLine(doc);

  let y = 30;

  // Brand mark
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  setColor(doc, ORANGE);
  doc.text('VANTAGE POINT MEDIA GROUP', PAGE_W / 2, y, { align: 'center' });
  y += 20;

  // Title
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  setColor(doc, DARK);
  doc.text('Your Content Growth', PAGE_W / 2, y, { align: 'center' });
  y += 12;
  doc.text('Roadmap', PAGE_W / 2, y, { align: 'center' });
  y += 10;

  // Subtitle
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  setColor(doc, MID_GRAY);
  doc.text(`Personalized strategy report for ${userData.fullName}`, PAGE_W / 2, y, { align: 'center' });
  y += 6;
  doc.text(`Generated on ${today}`, PAGE_W / 2, y, { align: 'center' });
  y += 18;

  // Score circle area
  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(MARGIN + 30, y, CONTENT_W - 60, 50, 4, 4, 'F');

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  setColor(doc, MID_GRAY);
  doc.text('CONTENT SYSTEM SCORE', PAGE_W / 2, y + 14, { align: 'center' });

  doc.setFontSize(42);
  doc.setFont('helvetica', 'bold');
  setColor(doc, ORANGE);
  doc.text(`${scoreData.total}`, PAGE_W / 2 - 5, y + 36, { align: 'center' });

  doc.setFontSize(18);
  setColor(doc, MID_GRAY);
  doc.text('/ 100', PAGE_W / 2 + 18, y + 36, { align: 'center' });

  y += 60;

  // Diagnosis
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  setColor(doc, DARK);
  const diagnosis = getPdfDiagnosis(scoreData.total);
  const diagLines = doc.splitTextToSize(diagnosis, CONTENT_W - 20);
  doc.text(diagLines, MARGIN + 10, y);
  y += diagLines.length * 5.5 + 12;

  // Business Snapshot
  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(MARGIN, y, CONTENT_W, 56, 4, 4, 'F');

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  setColor(doc, DARK);
  doc.text('Business Snapshot', MARGIN + 8, y + 10);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  setColor(doc, MID_GRAY);
  let sy = y + 20;
  const snapItems: [string, string | undefined][] = [
    ['90-Day Goal', quizAnswers.goal as string],
    ['Service Area', quizAnswers.service_area as string],
    ['Monthly Revenue', quizAnswers.monthly_revenue as string],
    ['Content Managed By', quizAnswers.responsibility as string],
  ];
  for (const [label, val] of snapItems) {
    if (!val) continue;
    doc.setFont('helvetica', 'bold');
    setColor(doc, DARK);
    doc.text(`${label}:`, MARGIN + 8, sy);
    doc.setFont('helvetica', 'normal');
    setColor(doc, MID_GRAY);
    doc.text(val, MARGIN + 55, sy);
    sy += 8;
  }

  // ===== PAGE 2: SCORE BREAKDOWN =====
  doc.addPage();
  addPageLine(doc);

  y = 24;
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  setColor(doc, DARK);
  doc.text('Your Score Breakdown', MARGIN, y);
  y += 14;

  const catEntries = Object.entries(scoreData.categories) as [string, number][];
  for (const [key, value] of catEntries) {
    // Category card
    doc.setFillColor(...LIGHT_BG);
    doc.roundedRect(MARGIN, y, CONTENT_W, 38, 3, 3, 'F');

    // Name + score
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    setColor(doc, DARK);
    doc.text(getCategoryLabel(key), MARGIN + 8, y + 10);

    doc.setFontSize(13);
    setColor(doc, ORANGE);
    doc.text(`${value} / 20`, MARGIN + CONTENT_W - 8, y + 10, { align: 'right' });

    // Progress bar
    drawProgressBar(doc, MARGIN + 8, y + 15, CONTENT_W - 16, 4, value / 20);

    // Explanation
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    setColor(doc, MID_GRAY);
    const explanation = getCategoryExplanation(key, value);
    const expLines = doc.splitTextToSize(explanation, CONTENT_W - 20);
    doc.text(expLines, MARGIN + 8, y + 25);

    y += 44;
  }

  // ===== PAGE 3: AREAS TO IMPROVE + ACTION PLAN =====
  doc.addPage();
  addPageLine(doc);

  y = 24;
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  setColor(doc, DARK);
  doc.text('Your Biggest Opportunities', MARGIN, y);
  y += 12;

  const weakAreas = getWeakAreas(scoreData.categories);
  for (let i = 0; i < weakAreas.length; i++) {
    const area = weakAreas[i];

    // Number badge
    doc.setFillColor(...ORANGE);
    doc.circle(MARGIN + 5, y + 2, 4, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    setColor(doc, WHITE);
    doc.text(`${i + 1}`, MARGIN + 5, y + 3.5, { align: 'center' });

    // Area name
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    setColor(doc, DARK);
    doc.text(area.name, MARGIN + 14, y + 3.5);
    y += 10;

    // Why it matters
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    setColor(doc, MID_GRAY);
    doc.text('Why it matters:', MARGIN + 6, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    const whyLines = doc.splitTextToSize(area.whyItMatters, CONTENT_W - 14);
    doc.text(whyLines, MARGIN + 6, y);
    y += whyLines.length * 4.5 + 4;

    // What to improve
    doc.setFont('helvetica', 'bold');
    doc.text('What to improve:', MARGIN + 6, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    for (const imp of area.improvements) {
      doc.text(`•  ${imp}`, MARGIN + 8, y);
      y += 5;
    }
    y += 6;
  }

  // Recommended Next Steps
  y += 4;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  setColor(doc, DARK);
  doc.text('Your Recommended Next Steps', MARGIN, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  setColor(doc, DARK);
  const nextSteps = [
    'Create 3–5 content pillars aligned with your ideal client',
    'Build a simple weekly posting system',
    'Use more trust-building and proof-based content',
    'Add a clear CTA and next step to every piece of content',
  ];
  for (const step of nextSteps) {
    setColor(doc, ORANGE);
    doc.text('✓', MARGIN + 4, y);
    setColor(doc, DARK);
    doc.text(step, MARGIN + 12, y);
    y += 7;
  }

  // ===== PAGE 4: CUSTOM STRATEGY SUMMARY =====
  doc.addPage();
  addPageLine(doc);

  y = 24;
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  setColor(doc, DARK);
  doc.text('Your Custom Strategy Summary', MARGIN, y);
  y += 14;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  setColor(doc, DARK);

  const summaryItems: string[] = [];
  if (quizAnswers.goal) summaryItems.push(`Your current 90-day goal is: ${quizAnswers.goal}`);
  if (quizAnswers.service_area) summaryItems.push(`Your business currently serves: ${quizAnswers.service_area}`);
  if (quizAnswers.responsibility) summaryItems.push(`Your content process is handled by: ${quizAnswers.responsibility}`);
  if (quizAnswers.bottleneck) summaryItems.push(`Your biggest bottleneck is: ${quizAnswers.bottleneck}`);

  const rec = getDynamicRecommendation(quizAnswers, scoreData.categories);
  summaryItems.push(`Based on your answers, the fastest win is to ${rec}.`);

  if (quizAnswers.business_description) {
    const desc = typeof quizAnswers.business_description === 'string' ? quizAnswers.business_description : '';
    if (desc) summaryItems.push(`Business: ${desc}`);
  }

  for (const item of summaryItems) {
    const lines = doc.splitTextToSize(`•  ${item}`, CONTENT_W - 10);
    doc.text(lines, MARGIN + 4, y);
    y += lines.length * 5.5 + 4;
  }

  // CTA section
  y += 10;
  doc.setFillColor(23, 23, 23);
  doc.roundedRect(MARGIN, y, CONTENT_W, 60, 4, 4, 'F');

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  setColor(doc, WHITE);
  doc.text('Want help implementing this roadmap?', PAGE_W / 2, y + 18, { align: 'center' });

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  setColor(doc, [200, 200, 200]);
  doc.text('We help businesses build content systems that increase', PAGE_W / 2, y + 28, { align: 'center' });
  doc.text('visibility, trust, and inbound leads.', PAGE_W / 2, y + 34, { align: 'center' });

  // CTA button
  doc.setFillColor(...ORANGE);
  const btnW = 70;
  const btnH = 12;
  const btnX = (PAGE_W - btnW) / 2;
  const btnY = y + 40;
  doc.roundedRect(btnX, btnY, btnW, btnH, 3, 3, 'F');
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  setColor(doc, WHITE);
  doc.text('Book a Free Strategy Call', PAGE_W / 2, btnY + 8, { align: 'center' });

  // Link
  doc.link(btnX, btnY, btnW, btnH, {
    url: 'https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ1t6kd1aJ-Zaxjdd-7hbwwUZ87lIIZvYC_PDVWu_g46xzt2fdNBZDIbIvESPrJsHDbx0LJTrMJq',
  });

  // Add footers
  addFooter(doc);

  return doc;
}

export function downloadRoadmapPDF(
  userData: UserData,
  quizAnswers: Record<string, string | string[]>,
  scoreData: ScoreData
): void {
  const doc = generateRoadmapPDF(userData, quizAnswers, scoreData);
  const firstName = userData.fullName.split(' ')[0]?.toLowerCase() || 'user';
  const date = new Date().toISOString().slice(0, 10);
  doc.save(`content-growth-roadmap-${firstName}-${date}.pdf`);
}

export function getRoadmapPDFBlob(
  userData: UserData,
  quizAnswers: Record<string, string | string[]>,
  scoreData: ScoreData
): Blob {
  const doc = generateRoadmapPDF(userData, quizAnswers, scoreData);
  return doc.output('blob');
}
